import { useMemo, useRef } from "react";

interface OtpInputProps {
  value: string;
  onChange: (v: string) => void;
  onComplete?: (v: string) => void;
  length?: number;
  charset?: "digits" | "alnum";
}

export function OtpInput({
  value,
  onChange,
  onComplete,
  length = 6,
  charset = "digits",
}: OtpInputProps) {
  const filter = useMemo(
    () =>
      (s: string): string => {
        const re = charset === "digits" ? /[0-9]/g : /[A-Za-z0-9]/g;
        const m = s.match(re);
        if (!m) {
          return "";
        }
        const joined = m.join("");
        return charset === "alnum" ? joined.toUpperCase() : joined;
      },
    [charset],
  );

  const slots = useMemo(() => {
    const cleaned = filter(value || "").slice(0, length);
    const arr = Array<string>(length).fill("");
    for (let i = 0; i < cleaned.length; i++) {
      arr[i] = cleaned[i] ?? "";
    }
    return arr;
  }, [value, length, filter]);

  const refs = useRef<Array<HTMLInputElement | null>>(Array(length).fill(null));

  const setRef =
    (i: number) =>
    (el: HTMLInputElement | null): void => {
      refs.current[i] = el;
    };

  const focusIndex = (i: number): void => {
    refs.current[i]?.focus();
  };

  const commit = (next: string[]): void => {
    const joined = next.join("");
    onChange(joined);
    if (joined.length === length && onComplete) {
      onComplete(joined);
    }
  };

  const onInputChange =
    (i: number) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const raw = e.target.value;
      const cleaned = filter(raw);
      const next = [...slots];

      if (cleaned.length > 1) {
        const toFill = cleaned.slice(0, length - i).split("");
        for (let k = 0; k < toFill.length; k++) {
          next[i + k] = toFill[k] ?? "";
        }
        commit(next);
        focusIndex(Math.min(i + toFill.length, length - 1));
        return;
      }

      next[i] = cleaned;
      commit(next);
      if (cleaned && i < length - 1) {
        focusIndex(i + 1);
      }
    };

  const onKeyDown =
    (i: number) =>
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === "Enter") {
        const joined = slots.join("");
        if (joined.length === length && onComplete) {
          e.preventDefault();
          onComplete(joined);
        }
        return;
      }

      if (e.key === "Backspace") {
        const next = [...slots];
        if (slots[i]) {
          next[i] = "";
          commit(next);
        } else if (i > 0) {
          focusIndex(i - 1);
          next[i - 1] = "";
          commit(next);
        }
        e.preventDefault();
        return;
      }

      if (e.key === "ArrowLeft" && i > 0) {
        focusIndex(i - 1);
        e.preventDefault();
        return;
      }
      if (e.key === "ArrowRight" && i < length - 1) {
        focusIndex(i + 1);
        e.preventDefault();
      }
    };

  const onPaste =
    (i: number) =>
    (e: React.ClipboardEvent<HTMLInputElement>): void => {
      const text = filter(e.clipboardData.getData("text"));
      if (!text) {
        return;
      }
      e.preventDefault();
      const next = [...slots];
      const chars = text.slice(0, length - i).split("");
      for (let k = 0; k < chars.length; k++) {
        next[i + k] = chars[k] ?? "";
      }
      commit(next);
      focusIndex(Math.min(i + chars.length, length - 1));
    };

  const inputMode = charset === "digits" ? "numeric" : "text";
  const pattern = charset === "digits" ? "[0-9]*" : "[A-Za-z0-9]*";

  return (
    <fieldset
      aria-label="Kod weryfikacyjny"
      className="flex items-center justify-center gap-2 border-none p-0 m-0"
    >
      {slots.map((d, i) => (
        <input
          // biome-ignore lint/suspicious/noArrayIndexKey: OTP slots are fixed-length and never reorder
          key={i}
          ref={setRef(i)}
          value={d}
          onChange={onInputChange(i)}
          onKeyDown={onKeyDown(i)}
          onPaste={onPaste(i)}
          inputMode={inputMode}
          pattern={pattern}
          autoComplete="one-time-code"
          spellCheck={false}
          aria-label={`Znak ${i + 1} z ${length}`}
          maxLength={1}
          className="h-12 w-12 rounded-lg border border-edge-hard bg-panel text-center font-mono text-xl tracking-widest text-main outline-none transition focus:border-adara-500 focus:ring-2 focus:ring-adara-200"
        />
      ))}
    </fieldset>
  );
}
